/**
 * @lodex-pro/web-portal/public/DisposalVerifyMagicLinkPage — magic-link consume UI
 * for the disposal-portal flow (DEC-160).
 *
 * Mirrors VerifyMagicLinkPage (operator, DEC-155). Renders when a user clicks
 * the verification link in the email sent by /v1/disposal-portal/magic-link/issue.
 * URL: /portal/disposal/verify?token=<opaque-token>
 *
 * Flow:
 *   1. On mount, read ?token= from URL via useSearchParams.
 *   2. If token missing → render error state with link back to /portal/disposal/register.
 *   3. If token present → fire POST /v1/disposal-portal/magic-link/consume with
 *      { token } on mount. (useRef guard prevents StrictMode double-fire.)
 *   4. Render states: loading, success (200), expired (410), invalid (400), error (other).
 *
 * NOTE: captchaToken NOT sent — consume endpoint has CAPTCHA removed per
 * DEC-155 grey area 2 (DEC-156 §5 keeps the same posture for disposal).
 *
 * Success copy differs from operator:
 *   "Awaiting admin approval and payment activation" (SWD's two-step flow)
 *   vs operator's "awaiting approval" (admin-only).
 * The SWD payment-activation amber banner is re-rendered on success so the
 * user sees the same context as on the register form.
 *
 * Works for both authenticated and unauthenticated users (public routes always
 * match first — grey area 4 resolution, DEC-155).
 *
 * Spec: DEC-155, DEC-156, DEC-160; master spec §49, §81.
 */

import React, { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDisposalMagicLinkConsumeMutation } from '../hooks/useDisposalMagicLinkConsumeMutation';
import { PublicApiError } from './publicApi';

function PaymentActivationNotice(): React.JSX.Element {
  return (
    <p
      role="note"
      style={{
        background: 'rgba(246, 182, 94, 0.14)',
        borderLeft: '3px solid var(--color-warning)',
        padding: '12px 16px',
        marginBottom: '20px',
        color: 'var(--color-warning)',
        fontSize: '14px',
        lineHeight: '1.5',
      }}
    >
      <strong>SWD portal activation is a two-step process.</strong> After Paelode admin approves
      your registration, a representative will contact you to complete billing setup. Portal access
      begins after payment activation.
    </p>
  );
}

export function DisposalVerifyMagicLinkPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const consumeMutation = useDisposalMagicLinkConsumeMutation();
  // Guard against StrictMode double-invocation — mutation should fire exactly once.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (token === null || token === '') return;
    firedRef.current = true;
    consumeMutation.mutate({ token });
    // consumeMutation is stable (TanStack Query identity) and token is a URL
    // param that won't change during the mount lifecycle. See VerifyMagicLinkPage
    // for the operator-side rationale — same treatment here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Missing token — show error immediately (no API call)
  if (token === null || token === '') {
    return (
      <div>
        <h1
          style={{
            fontSize: '22px',
            fontWeight: 700,
            marginBottom: '12px',
            color: 'var(--color-danger)',
          }}
        >
          Invalid link
        </h1>
        <p style={{ fontSize: '16px', color: 'var(--color-text-secondary)', marginBottom: '20px' }}>
          This URL is missing the verification token. Please use the link from your verification
          email.
        </p>
        <Link
          to="/portal/disposal/register"
          style={{ color: 'var(--color-link)', fontSize: '15px', textDecoration: 'underline' }}
        >
          Request a new registration link
        </Link>
      </div>
    );
  }

  // Loading state
  if (consumeMutation.isPending || consumeMutation.isIdle) {
    return (
      <div>
        <p role="status" style={{ fontSize: '16px', color: 'var(--color-text-secondary)' }}>
          Verifying your email…
        </p>
      </div>
    );
  }

  // Success state
  if (consumeMutation.isSuccess) {
    const { email } = consumeMutation.data;
    return (
      <div>
        <h1
          style={{
            fontSize: '22px',
            fontWeight: 700,
            marginBottom: '12px',
            color: 'var(--color-success)',
          }}
        >
          Email verified!
        </h1>
        <p style={{ fontSize: '16px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
          <strong style={{ color: 'var(--color-text-primary)' }}>{email}</strong> has been verified.
          Your registration is now awaiting admin approval and payment activation.
        </p>
        <p style={{ fontSize: '16px', color: 'var(--color-text-secondary)', marginBottom: '20px' }}>
          You&apos;ll receive an email once your portal access is activated.
        </p>
        <PaymentActivationNotice />
        <Link
          to="/portal/disposal/sign-in"
          style={{ color: 'var(--color-link)', fontSize: '15px', textDecoration: 'underline' }}
        >
          Return to sign in
        </Link>
      </div>
    );
  }

  // Error states
  if (consumeMutation.isError) {
    const err = consumeMutation.error;

    // 410 — expired token (DISPOSAL_MAGIC_LINK_TOKEN_EXPIRED)
    if (err instanceof PublicApiError && err.status === 410) {
      return (
        <div>
          <h1
            style={{
              fontSize: '22px',
              fontWeight: 700,
              marginBottom: '12px',
              color: 'var(--color-danger)',
            }}
          >
            Link expired
          </h1>
          <p
            style={{ fontSize: '16px', color: 'var(--color-text-secondary)', marginBottom: '20px' }}
          >
            This verification link has expired. Verification links are valid for 15 minutes.
          </p>
          <Link
            to="/portal/disposal/register"
            style={{ color: 'var(--color-link)', fontSize: '15px', textDecoration: 'underline' }}
          >
            Request a new registration link
          </Link>
        </div>
      );
    }

    // 400 DISPOSAL_MAGIC_LINK_TOKEN_INVALID_OR_CONSUMED — already used or unknown
    if (
      err instanceof PublicApiError &&
      err.code === 'DISPOSAL_MAGIC_LINK_TOKEN_INVALID_OR_CONSUMED'
    ) {
      return (
        <div>
          <h1
            style={{
              fontSize: '22px',
              fontWeight: 700,
              marginBottom: '12px',
              color: 'var(--color-danger)',
            }}
          >
            Link no longer valid
          </h1>
          <p
            style={{ fontSize: '16px', color: 'var(--color-text-secondary)', marginBottom: '20px' }}
          >
            This link is no longer valid. If you already verified your email, you can close this
            page. Otherwise, request a new registration link.
          </p>
          <Link
            to="/portal/disposal/register"
            style={{ color: 'var(--color-link)', fontSize: '15px', textDecoration: 'underline' }}
          >
            Request a new registration link
          </Link>
        </div>
      );
    }

    // 429 — rate-limited
    if (err instanceof PublicApiError && err.status === 429) {
      return (
        <div>
          <h1
            style={{
              fontSize: '22px',
              fontWeight: 700,
              marginBottom: '12px',
              color: 'var(--color-danger)',
            }}
          >
            Too many attempts
          </h1>
          <p style={{ fontSize: '16px', color: 'var(--color-text-secondary)' }}>
            Too many verification attempts. Please wait a few minutes and try again.
          </p>
        </div>
      );
    }

    // Generic error
    return (
      <div>
        <h1
          style={{
            fontSize: '22px',
            fontWeight: 700,
            marginBottom: '12px',
            color: 'var(--color-danger)',
          }}
        >
          Something went wrong
        </h1>
        <p style={{ fontSize: '16px', color: 'var(--color-text-secondary)', marginBottom: '20px' }}>
          We couldn&apos;t verify your email right now. Try again shortly.
        </p>
        <Link
          to="/portal/disposal/register"
          style={{ color: 'var(--color-link)', fontSize: '15px', textDecoration: 'underline' }}
        >
          Return to registration
        </Link>
      </div>
    );
  }

  return <></>;
}
